import OCGatewayClient from "../client/OCGatewayClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Device } from "../models/Device";
import { gatewayRealtimeQueryOptions, mergeUpdateBodyIntoCachedObject } from "./gatewayQueryUtils";

let subscribed = false;

export function useDeviceQuery(deviceId: string) {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: ['getDevice', deviceId],
        enabled: !!deviceId,
        queryFn: async (): Promise<any> => {
            return OCGatewayClient.request('getDevice', {
                deviceId,
                subscribe: true
            });
        },
        ...gatewayRealtimeQueryOptions
    });

    if (!subscribed) {
        OCGatewayClient.socket.on('deviceUpdate', (data: any) => {
            queryClient.setQueryData(['getDevice', data.deviceId], (device: Device) => {
                return mergeUpdateBodyIntoCachedObject(device, data, 'deviceUpdate');
            });
        });

        subscribed = true;
    }

    return query;
}

export function useUpdateDeviceMutation(deviceId: string) {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: async (device: Partial<Device>) => {
            await OCGatewayClient.request('updateDevice', {
                deviceId,
                ...device
            });
            return device;
        },
        onMutate: async (device: Partial<Device>) => {
            await queryClient.cancelQueries({ queryKey: ['getDevice', deviceId] });

            const previousDevice = queryClient.getQueryData(['getDevice', deviceId]) as Device;

            queryClient.setQueryData(['getDevice', deviceId], (queryData: any) => {
                return Object.assign({}, queryData, device);
            });

            return { previousDevice };
        },
        onError: (error, variables, context) => {
            if (context?.previousDevice) {
                queryClient.setQueryData(['getDevice', deviceId], context.previousDevice);
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['getDevice', deviceId] });
        }
    });

    return mutation;
}
